import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { LogOut, LogIn, Plus, Menu, X, MessageCircle, Mail, Shield, MessagesSquare, Search, ShoppingBag, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import NotificationsBell from "@/components/NotificationsBell";
import logoImg from "@/assets/logo.webp";
import { clearHomeFeedCache } from "@/lib/feed-cache";

export default function Navbar() {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");
  const activeClass = "text-primary font-semibold";

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const loadUnread = async () => {
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("to_user_id", user.id)
        .eq("is_read", false);
      setUnreadCount(count || 0);
    };
    loadUnread();
    const channel = supabase
      .channel("navbar-unread")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages", filter: `to_user_id=eq.${user.id}` }, () => loadUnread())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => {
    if (!user) { setAvatarUrl(null); setDisplayName(null); return; }
    const loadProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("avatar_url, username")
        .eq("user_id", user.id)
        .single();
      setAvatarUrl(data?.avatar_url || null);
      setDisplayName(data?.username || null);
    };
    loadProfile();
  }, [user]);

  const getInitials = () => {
    if (!displayName) return "?";
    return displayName.slice(0, 2).toUpperCase();
  };

  return (
    <nav className="fixed inset-x-0 top-0 z-50 border-b bg-surface/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between md:h-20">
        <Link
          to="/"
          className="flex items-center"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              clearHomeFeedCache();
              window.scrollTo({ top: 0, left: 0, behavior: "auto" });
              window.location.reload();
            } else {
              window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            }
          }}
          aria-label="LPU Football Shirts"
        >
          <img src={logoImg} alt="LPU Football Shirts" className="h-10 w-auto sm:h-11 md:h-14 object-contain" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 md:flex">
          {user && (
            <>
              <Link to="/explore">
                <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive("/explore") ? activeClass : ""}`}>
                  <Search className="h-4 w-4" />
                  Przeglądaj koszulki
                </Button>
              </Link>
              <Link to="/na-sprzedaz">
                <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive("/na-sprzedaz") ? activeClass : ""}`}>
                  <ShoppingBag className="h-4 w-4" />
                  Na sprzedaż
                </Button>
              </Link>
              <Link to="/pogadajmy">
                <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive("/pogadajmy") ? activeClass : ""}`}>
                  <MessagesSquare className="h-4 w-4" />
                  Pogadajmy
                </Button>
              </Link>
            </>
          )}
          {user ? (
            <>
              <Link to="/collectors">
                <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive("/collectors") ? activeClass : ""}`}>
                  <Users className="h-4 w-4" />
                  Kolekcjonerzy
                </Button>
              </Link>
              <Link to="/add-kit">
                <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive("/add-kit") ? activeClass : ""}`}>
                  <Plus className="h-4 w-4" /> Dodaj koszulkę
                </Button>
              </Link>
              <Link to="/wiadomosci">
                <Button variant="ghost" size="sm" className={`relative px-2.5 gap-1.5 ${isActive("/wiadomosci") ? activeClass : ""}`}>
                  <MessageCircle className="h-4 w-4" />
                  Wiadomości
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </Link>
              <div className="shrink-0">
                <NotificationsBell />
              </div>
              <div className="mx-0.5 h-5 w-px bg-border" />
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive("/admin") ? activeClass : ""}`}>
                    <Shield className="h-4 w-4" />
                    Admin
                  </Button>
                </Link>
              )}
              <Link to={`/profile/${user.id}`}>
                <Button variant="ghost" size="sm" className={`px-2.5 gap-1.5 ${isActive(`/profile/${user.id}`) ? activeClass : ""}`}>
                  <Avatar className="h-8 w-8">
                    {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName || "Avatar"} />}
                    <AvatarFallback className="text-[10px] bg-muted">{getInitials()}</AvatarFallback>
                  </Avatar>
                  Mój profil
                </Button>
              </Link>
              <Button variant="ghost" size="icon" aria-label="Wyloguj" title="Wyloguj" onClick={() => signOut().then(() => navigate("/"))}>
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/auth">
                <Button size="sm">Zaloguj się</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile: bell + hamburger */}
        <div className="flex items-center gap-1 md:hidden">
          {user && !mobileOpen && (
            <>
              <Link to="/wiadomosci" className="shrink-0">
                <Button variant="ghost" size="icon" aria-label="Wiadomości" className="relative">
                  <Mail className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </Link>
              <div className="shrink-0">
                <NotificationsBell />
              </div>
              <Link to={`/profile/${user.id}`} className="shrink-0" aria-label="Mój profil">
                <Avatar className="h-8 w-8">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName || "Avatar"} />}
                  <AvatarFallback className="text-[10px] bg-muted">{getInitials()}</AvatarFallback>
                </Avatar>
              </Link>
            </>
          )}
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t bg-surface p-4 md:hidden animate-fade-in">
          <div className="flex flex-col gap-2">
            {user && (
              <>
                <Link to="/explore" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" className={`w-full justify-start gap-2 ${isActive("/explore") ? "text-primary font-semibold" : ""}`}>
                    <Search className="h-4 w-4" /> Przeglądaj koszulki
                  </Button>
                </Link>
                <Link to="/na-sprzedaz" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" className={`w-full justify-start gap-2 ${isActive("/na-sprzedaz") ? "text-primary font-semibold" : ""}`}>
                    <ShoppingBag className="h-4 w-4" /> Na sprzedaż
                  </Button>
                </Link>
                <Link to="/pogadajmy" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" className={`w-full justify-start gap-2 ${isActive("/pogadajmy") ? "text-primary font-semibold" : ""}`}>
                    <MessagesSquare className="h-4 w-4" /> Pogadajmy
                  </Button>
                </Link>
              </>
            )}
            {user ? (
              <>
                <Link to="/collectors" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" className={`w-full justify-start gap-2 ${isActive("/collectors") ? "text-primary font-semibold" : ""}`}>
                    <Users className="h-4 w-4" /> Kolekcjonerzy
                  </Button>
                </Link>
                <Link to="/add-kit" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" className={`w-full justify-start gap-2 ${isActive("/add-kit") ? "text-primary font-semibold" : ""}`}>
                    <Plus className="h-4 w-4" /> Dodaj koszulkę
                  </Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className={`w-full justify-start gap-2 ${isActive("/admin") ? "text-primary font-semibold" : ""}`}>
                      <Shield className="h-4 w-4" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => { signOut(); setMobileOpen(false); navigate("/"); }}>
                  <LogOut className="h-4 w-4" /> Wyloguj się
                </Button>
              </>
            ) : (
              <>
                <Link to="/auth" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full justify-start gap-2"><LogIn className="h-4 w-4" /> Zaloguj się</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
